import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Database,
  Server,
  Activity,
  Shield,
  Globe,
  Cpu,
  HardDrive,
  Users,
  Settings,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';
import { PRODUCTION_CONFIG, isProduction } from '@/config/production';
import { corsRequest } from '@/config/cors';
import { useUser } from '@/contexts/UserContext';
import { useLocation } from 'wouter';

interface ServiceStatus {
  name: string;
  url: string;
  status: 'healthy' | 'error' | 'warning' | 'loading' | 'checking';
  responseTime?: number;
  lastChecked?: string;
  details?: any;
  error?: string;
  description?: string;
  category?: 'frontend' | 'backend' | 'database' | 'storage' | 'processing';
}

interface DatabaseInfo {
  provider: string;
  type: string;
  url: string;
  status: string;
  hasAnonKey: boolean;
  hasServiceKey: boolean;
  hasAccessToken: boolean;
  project?: {
    projectRef: string;
    name?: string;
    region?: string;
    status?: string;
    organizationId?: string;
    isLocal: boolean;
    isCloud: boolean;
  };
  organization?: {
    id: string;
    name: string;
    slug: string;
    created_at: string;
  };
  capabilities?: {
    hasAuth: boolean;
    hasStorage: boolean;
    hasRealtime: boolean;
    hasFromMethod: boolean;
    hasRpcMethod: boolean;
    hasSelectMethod: boolean;
    canConnect: boolean;
    error?: string;
  };
}

interface SystemHealth {
  status: string;
  timestamp: string;
  uptime: number;
  environment: string;
  version: string;
  database: DatabaseInfo;
  services: {
    supabase: string;
    python: string;
    storage: string;
  };
}

const SystemStatus: React.FC = () => {
  const { user } = useUser();
  const [, setLocation] = useLocation();
  const isAdmin = user?.role === 'admin';

  const [services, setServices] = useState<ServiceStatus[]>([
    {
      name: 'Frontend',
      url: isProduction() ? PRODUCTION_CONFIG.HEALTH_ENDPOINTS.FRONTEND : 'http://localhost:3000/health.json',
      status: 'checking',
      description: 'Interface do usuário e aplicação web',
      category: 'frontend'
    },
    {
      name: 'Backend API',
      url: isProduction() ? PRODUCTION_CONFIG.HEALTH_ENDPOINTS.API : 'http://localhost:3001/api/health',
      status: 'checking',
      description: 'API principal do sistema',
      category: 'backend'
    },
    {
      name: 'PyImg Service',
      url: isProduction() ? PRODUCTION_CONFIG.HEALTH_ENDPOINTS.API : 'http://localhost:3001/api/pyimg/health',
      status: 'checking',
      description: 'Serviço de análise de imagens com Python',
      category: 'processing'
    }
  ]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Não exigir autenticação - página pública

  const checkServiceHealth = async (service: ServiceStatus): Promise<ServiceStatus> => {
    const startTime = Date.now();

    try {
      const response = await corsRequest(service.url, {
        method: 'GET',
        // Timeout de 10 segundos
        signal: AbortSignal.timeout(10000)
      });

      const responseTime = Date.now() - startTime;

      // Tenta fazer parse como JSON, mas aceita texto também
      let data;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Se não for JSON, tenta extrair informações do texto
        const text = await response.text();
        try {
          data = JSON.parse(text);
        } catch {
          // Se não conseguir fazer parse, cria um objeto básico
          data = {
            status: response.ok ? 'ok' : 'error',
            message: text,
            service: service.name.toLowerCase()
          };
        }
      }

      // Se for o backend API, salvar informações detalhadas do sistema
      if (service.name === 'Backend API' && response.ok && data) {
        setSystemHealth(data);
      }

      if (response.ok) {
        return {
          ...service,
          status: 'healthy',
          responseTime,
          lastChecked: new Date().toISOString(),
          details: data,
          error: undefined
        };
      } else {
        return {
          ...service,
          status: 'error',
          responseTime,
          lastChecked: new Date().toISOString(),
          error: `HTTP ${response.status}: ${response.statusText}`,
          details: data
        };
      }
    } catch (error) {
      const responseTime = Date.now() - startTime;
      return {
        ...service,
        status: 'error',
        responseTime,
        lastChecked: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'Unknown error',
        details: undefined
      };
    }
  };

  const checkAllServices = async () => {
    setIsRefreshing(true);

    const updatedServices = await Promise.all(
      services.map(service => checkServiceHealth(service))
    );

    setServices(updatedServices);
    setLastUpdate(new Date());
    setIsRefreshing(false);
  };

  useEffect(() => {
    checkAllServices();
  }, []);

  const getStatusIcon = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-primary" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'loading':
        return <Clock className="h-5 w-5 text-gray-500 animate-pulse" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'healthy':
        return <Badge variant="default" className="bg-primary">Operacional</Badge>;
      case 'error':
        return <Badge variant="destructive">Erro</Badge>;
      case 'warning':
        return <Badge variant="secondary" className="bg-yellow-500">Atenção</Badge>;
      case 'loading':
        return <Badge variant="outline">Verificando...</Badge>;
      default:
        return <Badge variant="outline">Desconhecido</Badge>;
    }
  };

  const getCategoryIcon = (category: ServiceStatus['category']) => {
    switch (category) {
      case 'frontend':
        return <Globe className="h-4 w-4" />;
      case 'backend':
        return <Server className="h-4 w-4" />;
      case 'database':
        return <Database className="h-4 w-4" />;
      case 'storage':
        return <HardDrive className="h-4 w-4" />;
      case 'processing':
        return <Cpu className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getHealthPercentage = () => {
    const healthyCount = services.filter(s => s.status === 'healthy').length;
    return Math.round((healthyCount / services.length) * 100);
  };

  const healthyServices = services.filter(s => s.status === 'healthy').length;
  const totalServices = services.length;
  const healthPercentage = getHealthPercentage();

  // Página pública - não há restrição de acesso

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Monitoramento do Sistema
            </h1>
            <p className="text-muted-foreground mt-2">
              Dashboard de status em tempo real dos microserviços FotUp
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Última atualização</div>
              <div className="text-sm font-medium">{lastUpdate.toLocaleTimeString('pt-BR')}</div>
            </div>
            <Button
              onClick={checkAllServices}
              disabled={isRefreshing}
              variant="outline"
              size="lg"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </div>

        {/* Status Overview Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status Geral</p>
                  <p className="text-2xl font-bold text-primary">{healthPercentage}%</p>
                </div>
                <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                  {healthyServices === totalServices ? (
                    <CheckCircle className="h-6 w-6 text-primary" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600" />
                  )}
                </div>
              </div>
              <Progress value={healthPercentage} className="mt-4" />
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Serviços Ativos</p>
                  <p className="text-2xl font-bold text-blue-600">{healthyServices}/{totalServices}</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Activity className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Uptime</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {systemHealth ? formatUptime(systemHealth.uptime) : 'N/A'}
                  </p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Ambiente</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {systemHealth?.environment || 'N/A'}
                  </p>
                </div>
                <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Settings className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for detailed information */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="services">Serviços</TabsTrigger>
            {isAdmin && <TabsTrigger value="database">Banco de Dados</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Service Status Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {services.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        {getCategoryIcon(service.category)}
                        {service.name}
                      </CardTitle>
                      {getStatusBadge(service.status)}
                    </div>
                    <CardDescription>
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {service.responseTime && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Tempo de Resposta:</span>
                        <span className={`font-medium ${service.responseTime > 1000 ? 'text-yellow-600' : 'text-primary'}`}>
                          {service.responseTime}ms
                        </span>
                      </div>
                    )}

                    {service.lastChecked && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Última Verificação:</span>
                        <span className="font-medium">
                          {new Date(service.lastChecked).toLocaleTimeString('pt-BR')}
                        </span>
                      </div>
                    )}

                    {service.error && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" />
                          <strong>Erro:</strong>
                        </div>
                        <p className="mt-1">{service.error}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="services" className="space-y-6">
            <div className="grid gap-6">
              {services.map((service, index) => (
                <Card key={index}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        {getCategoryIcon(service.category)}
                        {service.name}
                      </CardTitle>
                      {getStatusBadge(service.status)}
                    </div>
                    <CardDescription className="break-all">
                      {service.url}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      {service.responseTime && (
                        <div className="space-y-1">
                          <p className="text-sm font-medium">Tempo de Resposta</p>
                          <p className={`text-lg font-bold ${service.responseTime > 1000 ? 'text-yellow-600' : 'text-primary'}`}>
                            {service.responseTime}ms
                          </p>
                        </div>
                      )}

                      {service.lastChecked && (
                        <div className="space-y-1">
                          <p className="text-sm font-medium">Última Verificação</p>
                          <p className="text-lg font-bold">
                            {new Date(service.lastChecked).toLocaleTimeString('pt-BR')}
                          </p>
                        </div>
                      )}
                    </div>

                    {service.error && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center gap-2 text-red-700 font-medium">
                          <AlertTriangle className="h-4 w-4" />
                          Erro Detectado
                        </div>
                        <p className="mt-2 text-red-600">{service.error}</p>
                      </div>
                    )}

                    {service.details && (
                      <details className="group">
                        <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-800 group-open:text-blue-800">
                          Ver Detalhes da Resposta
                        </summary>
                        <div className="mt-3 p-4 bg-gray-50 border rounded-lg">
                          <pre className="text-xs overflow-auto max-h-64 whitespace-pre-wrap">
                            {JSON.stringify(service.details, null, 2)}
                          </pre>
                        </div>
                      </details>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {isAdmin && (
            <TabsContent value="database" className="space-y-6">
              {systemHealth?.database ? (
                <div className="space-y-6">
                  {/* Database Overview */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Database className="h-5 w-5" />
                        Informações do Banco de Dados
                      </CardTitle>
                      <CardDescription>
                        Status detalhado da conexão com Supabase
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-muted-foreground">Provedor</p>
                          <p className="text-lg font-bold">{systemHealth.database.provider}</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-muted-foreground">Tipo</p>
                          <p className="text-lg font-bold">{systemHealth.database.type}</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-muted-foreground">Status</p>
                          <Badge variant={systemHealth.database.status.includes('pronto') ? 'default' : 'destructive'}>
                            {systemHealth.database.status}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-muted-foreground">URL</p>
                          <p className="text-sm break-all">{systemHealth.database.url}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Project Information */}
                  {systemHealth.database.project && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Server className="h-5 w-5" />
                          Informações do Projeto
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-muted-foreground">ID do Projeto</p>
                            <p className="text-lg font-bold font-mono">{systemHealth.database.project.projectRef}</p>
                          </div>
                          {systemHealth.database.project.name && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-muted-foreground">Nome</p>
                              <p className="text-lg font-bold">{systemHealth.database.project.name}</p>
                            </div>
                          )}
                          {systemHealth.database.project.region && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-muted-foreground">Região</p>
                              <p className="text-lg font-bold">{systemHealth.database.project.region}</p>
                            </div>
                          )}
                          {systemHealth.database.project.status && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-muted-foreground">Status</p>
                              <Badge variant={systemHealth.database.project.status === 'active' ? 'default' : 'secondary'}>
                                {systemHealth.database.project.status}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Organization Information */}
                  {systemHealth.database.organization && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="h-5 w-5" />
                          Informações da Organização
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-muted-foreground">Nome</p>
                            <p className="text-lg font-bold">{systemHealth.database.organization.name}</p>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-muted-foreground">Slug</p>
                            <p className="text-lg font-bold">{systemHealth.database.organization.slug}</p>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-muted-foreground">ID</p>
                            <p className="text-sm font-mono">{systemHealth.database.organization.id}</p>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-muted-foreground">Criado em</p>
                            <p className="text-sm">{new Date(systemHealth.database.organization.created_at).toLocaleDateString('pt-BR')}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Capabilities */}
                  {systemHealth.database.capabilities && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Shield className="h-5 w-5" />
                          Capacidades do Cliente
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium">Autenticação</span>
                            <Badge variant={systemHealth.database.capabilities.hasAuth ? 'default' : 'destructive'}>
                              {systemHealth.database.capabilities.hasAuth ? 'Disponível' : 'Indisponível'}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium">Storage</span>
                            <Badge variant={systemHealth.database.capabilities.hasStorage ? 'default' : 'destructive'}>
                              {systemHealth.database.capabilities.hasStorage ? 'Disponível' : 'Indisponível'}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium">Realtime</span>
                            <Badge variant={systemHealth.database.capabilities.hasRealtime ? 'default' : 'destructive'}>
                              {systemHealth.database.capabilities.hasRealtime ? 'Disponível' : 'Indisponível'}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium">Conexão</span>
                            <Badge variant={systemHealth.database.capabilities.canConnect ? 'default' : 'destructive'}>
                              {systemHealth.database.capabilities.canConnect ? 'Ativa' : 'Inativa'}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Keys Status */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Status das Chaves de Acesso
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-3 md:grid-cols-3">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium">Chave Anônima</span>
                          <Badge variant={systemHealth.database.hasAnonKey ? 'default' : 'destructive'}>
                            {systemHealth.database.hasAnonKey ? 'Configurada' : 'Ausente'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium">Chave de Serviço</span>
                          <Badge variant={systemHealth.database.hasServiceKey ? 'default' : 'destructive'}>
                            {systemHealth.database.hasServiceKey ? 'Configurada' : 'Ausente'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium">Token de Acesso</span>
                          <Badge variant={systemHealth.database.hasAccessToken ? 'default' : 'destructive'}>
                            {systemHealth.database.hasAccessToken ? 'Configurado' : 'Ausente'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Database className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Informações do Banco de Dados Indisponíveis</h3>
                    <p className="text-muted-foreground">
                      As informações detalhadas do banco de dados serão exibidas após a verificação do backend.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default SystemStatus;